import React from "react";
import {
  UseFormRegister,
  FieldErrors,
  Control,
  useFieldArray,
} from "react-hook-form";
import { Plus, Trash2, FileText } from "lucide-react";
import Button from "../UI/Button";
import { Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { BL_REMARK_LOCATION_OPTIONS } from "../../constants/shipmentFormConstants";
import { useTranslation } from "react-i18next";

interface BLRemarksSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  control: Control<CreateShipmentData>;
}

// B/L Remarks (SEA only) — an optional list that can hold 0, 1, or many rows.
export const BLRemarksSection: React.FC<BLRemarksSectionProps> = ({
  register,
  readonly,
  control,
}) => {
  const { t } = useTranslation("shipments");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "seaDetails.blRemarks",
  });

  const th =
    "px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider";
  const td = "px-2 py-1.5 align-top";

  return (
    <SectionCard
      icon={FileText}
      title={t("blRemarks.title")}
      actions={
        !readonly && (
          <Button
            type="button"
            onClick={() => append({ location: "body_free", text: "" })}
            variant="primary"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            {t("blRemarks.addRemark")}
          </Button>
        )
      }
    >
      {fields.length === 0 ? (
        <p className="text-[12px] text-gray-400">{t("blRemarks.empty")}</p>
      ) : (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className={`${th} w-56`}>{t("blRemarks.location")}</th>
                  <th className={th}>{t("blRemarks.title")}</th>
                  {!readonly && (
                    <th className={`${th} w-12 text-right`}>
                      {t("cargo.action")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {fields.map((field, index) => (
                  <tr
                    key={field.id}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className={`${td} w-56`}>
                      <Select
                        {...register(
                          `seaDetails.blRemarks.${index}.location` as const,
                        )}
                        options={BL_REMARK_LOCATION_OPTIONS}
                        disabled={readonly}
                        size="sm"
                        placeholder={t("blRemarks.locationPlaceholder")}
                        className="w-full"
                      />
                    </td>
                    <td className={td}>
                      <Input
                        {...register(
                          `seaDetails.blRemarks.${index}.text` as const,
                        )}
                        readOnly={readonly}
                        size="sm"
                        placeholder={t("blRemarks.placeholder")}
                        className="w-full"
                      />
                    </td>
                    {!readonly && (
                      <td className={`${td} text-right`}>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

BLRemarksSection.displayName = "BLRemarksSection";
